import { flow } from '@1x2network/flow'
import { Spine, ValueText, ValueTextSpecification } from '@1x2network/pixi_assets'
import { BasicSymbol, BasicSymbolSpec } from '@1x2network/reel_set'
import { formatPixiText } from './utils/formatCurrency'
import { Container } from 'pixi.js'
import { displayedValues } from './constants/valueIds'

/**
 * A basic symbol with an extra collect animation
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class CustomSymbol extends BasicSymbol {
    private _text?: ValueText
    public stakeSub?: () => void
    public readonly animations: {
        static: string
        activate: string
        land: string
        win: string
        collect: string
    }

    constructor(options: { spec?: CustomSymbolSpecification; id: number }) {
        super({ spec: options.spec, id: options.id })
        this.sortableChildren = true
        this.animations = options.spec?.animations ?? {
            static: 'static',
            activate: 'win',
            land: 'landing',
            win: 'win',
            collect: 'landing',
        }
        if (options.spec?.value) {
            this._text = new ValueText({ spec: options.spec.value, parent: this, format: formatPixiText })
        }

        if (this.spine) {
            const spine = this.spine as Spine
            spine.state.addListener({
                complete: (entry) => {
                    if (!entry.loop && entry.animation.duration) {
                        spine.pose(this.animations.static, 0)
                    }
                },
            })
        }
    }

    public async playLanding(): Promise<void> {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.land))
        }
    }

    public async playCollect(): Promise<void> {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.collect))
        }
    }

    public async playActivate(): Promise<void> {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.activate))
        }
    }

    /** play win animation on the symbol */
    public async playWin() {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.win))
        }
    }

    public updateTextValue(cash: number) {
        if (this._text && displayedValues.includes(this.id)) {
            this._text.value = cash
            this._text.visible = Boolean(cash)
        }
    }

    public addBackgroundEffect(container: Container) {
        this.addChild(container)
    }

    public get value(): number {
        return this._text?.value ?? 0
    }

    public reset() {
        super.reset()
        if (this.stakeSub) {
            this.stakeSub()
        }
        this.tint = 0xffffff
        this.alpha = 1
        this.visible = true
        this.scale.set(1)
        this.position.set(0)
        this.zIndex = 0
        this.name = null
        if (this.text) {
            this.text.visible = false
        }
    }

    public set tint(value: number) {
        const spine = this.spine
        if (!spine) return

        spine.tint = value
        if (!spine.autoUpdate) {
            spine.update(0)
        }
    }

    public get tint(): number {
        const spine = this.spine
        if (!spine) return 0xffffff
        return spine.tint
    }

    public get text() {
        return this._text
    }
}

export interface CustomSymbolSpecification extends BasicSymbolSpec {
    value?: ValueTextSpecification
    animations: {
        static: string
        activate: string
        land: string
        win: string
        collect: string
    }
}
